/**
 * API Configuration
 * 
 * Centralized API endpoints for static frontend.
 * These can be overridden at runtime via window.__ENFA_API_CONFIG__.
 * 
 * Canonical API (enfa-api) runs on port 6082.
 * Browser calls from client UI must use same-origin "/api/*" and let the SSR BFF
 * forward to enfa-api. This avoids CORS preflight regressions in local/dev.
 * 
 * IMPORTANT: Cliente web debe usar llamadas same-origin para /api/*
 * No se debe configurar host absoluto aquí.
 * El BFF de client maneja el proxy a enfa-api (:6082)
 */

export interface ApiConfig {
  clientApi: string;
  employeesApi: string;
  api: string;
}

/**
 * Detect if we're in development mode
 */
function isDevelopment(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

/**
 * Default API base URLs
 * 
 * In both development and production, client browser requests stay relative.
 * The client SSR service is responsible for transport to enfa-api.
 */
const DEV_CONFIG: ApiConfig = {
  clientApi: '',  // Keep relative for client endpoints (session cookies)
  employeesApi: 'http://localhost:6081',
  api: '',  // Keep relative and use client BFF proxy
};

const PROD_CONFIG: ApiConfig = {
  clientApi: '',
  employeesApi: '',
  api: '',  // Keep relative in production (reverse proxy handles routing)
};

const DEFAULT_CONFIG: ApiConfig = isDevelopment() ? DEV_CONFIG : PROD_CONFIG;

/**
 * Get API configuration
 */
export function getApiConfig(): ApiConfig {
  // Check if overrides exist in window (for dynamic/runtime changes)
  const windowConfig = (window as any).__ENFA_API_CONFIG__;

  if (windowConfig) {
    return {
      ...DEFAULT_CONFIG,
      ...windowConfig,
    };
  }

  return DEFAULT_CONFIG;
}

/**
 * Get API base URL by service type
 */
export function getApiUrl(service: 'client' | 'employees' | 'api'): string {
  const config = getApiConfig();

  switch (service) {
    case 'client':
      return config.clientApi;
    case 'employees':
      return config.employeesApi;
    case 'api':
      return config.api;
    default:
      throw new Error(`Unknown service type: ${service}`);
  }
}

/**
 * Build full endpoint URL
 */
export function getApiEndpoint(service: 'client' | 'employees' | 'api', path: string): string {
  const baseUrl = getApiUrl(service);
  return `${baseUrl}${path}`;
}

// Export defaults
export const { clientApi, employeesApi, api } = getApiConfig();
